import { A, G } from "@mobily/ts-belt";
import PropTypes from "prop-types";
import * as React from "react";
import { useUsers } from "../../../hooks";
import {
  Autocomplete,
  Button,
  IconButton,
  Spinner,
  TextInput,
  Typography,
} from "../../../themeV2";
import { DeleteIcon } from "../../../themeV2";
import { dateTimeFormatter } from "../../../util";
import {
  MAX_SALESPEOPLE,
  useSalespersons,
  updateSalespeople,
} from "../../ShopSalespersons/utils";
import styles from "./SalespersonsContent.module.css";

const salespersonParams = { is_salesperson: true };

const nameFor = (user) => {
  const parts = [user.firstName, user.lastName].filter(Boolean);
  return parts.length === 0 ? "No Name" : parts.join(" ");
};

const SalespersonsContent = React.memo(function SalespersonsContent({
  influencerId,
}) {
  const {
    isError,
    isLoading: isLoadingSalespersons,
    mutate,
    salespersons,
  } = useSalespersons(influencerId);

  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const { users } = useUsers(salespersonParams);

  const setSalespersons = React.useCallback(
    async (workingSalespersons) => {
      const optimisticData = G.isFunction(workingSalespersons)
        ? workingSalespersons(salespersons)
        : workingSalespersons;

      setLoading(true);

      try {
        await mutate(
          updateSalespeople({ influencerId, salespersons: optimisticData }),
          {
            optimisticData: { salespersons: optimisticData },
            rollbackOnError(err) {
              console.error(err);
              return err.name !== "AbortError";
            },
          },
        );
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [salespersons, influencerId, mutate],
  );

  const handleAdd = () => {
    if (!user || salespersons.length >= MAX_SALESPEOPLE) return;

    const newSalesperson = {
      userId: user.userId,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      fullName: user.fullName,
      created: user.created,
      percentage: 100,
    };

    setUser(null);
    setSalespersons(A.append(newSalesperson));
  };

  const handleDelete = (salesperson) => {
    setSalespersons((current) => current.filter((sp) => sp !== salesperson));
  };

  const isLoading = isLoadingSalespersons || loading;

  if (isLoadingSalespersons) {
    return (
      <div className={styles.center}>
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <Typography
        variant="body-lg"
        color="variant"
        className={styles.description}
      >
        Manage salespeople associated with this shop. Each salesperson receives
        a percentage of sales.
      </Typography>

      {salespersons.length === 0 ? (
        <Typography variant="body-lg" color="variant">
          No salespeople.
        </Typography>
      ) : (
        <div className={styles.list}>
          {salespersons.map((salesperson) => (
            <div key={salesperson.userId} className={styles.salespersonRow}>
              <div className={styles.salespersonInfo}>
                <Typography variant="body-lg">
                  {salesperson.fullName ?? "No name"} ({salesperson.email})
                </Typography>
                <Typography variant="body-md" color="variant">
                  {dateTimeFormatter.format(salesperson.created)}
                </Typography>
              </div>

              <TextInput
                label="Percentage"
                type="number"
                disabled
                value={String(salesperson.percentage)}
                style={{ width: 100 }}
              />

              <IconButton
                aria-label="Delete salesperson"
                onClick={() => handleDelete(salesperson)}
                disabled={isLoading}
              >
                <DeleteIcon />
              </IconButton>
            </div>
          ))}
        </div>
      )}

      {salespersons.length < MAX_SALESPEOPLE && (
        <div className={styles.addSection}>
          <Autocomplete.Root
            label="Salesperson"
            placeholder="Search salespersons…"
            value={user}
            onValueChange={(val) => setUser(val)}
            itemToStringLabel={(u) => u?.email ?? ""}
            disabled={isLoading || isError}
          >
            {users.map((u) => (
              <Autocomplete.Item key={u.userId} value={u}>
                <div>
                  <Typography variant="body-md">{nameFor(u)}</Typography>
                  <Typography variant="label-md" color="variant">
                    {u.email}
                  </Typography>
                </div>
              </Autocomplete.Item>
            ))}
            <Autocomplete.Empty>No salespersons found</Autocomplete.Empty>
          </Autocomplete.Root>

          <Button
            onClick={handleAdd}
            disabled={
              !user ||
              isLoading ||
              isError ||
              salespersons.length >= MAX_SALESPEOPLE
            }
          >
            Add
          </Button>
        </div>
      )}
    </div>
  );
});

SalespersonsContent.displayName = "SalespersonsContent";

SalespersonsContent.propTypes = {
  influencerId: PropTypes.number.isRequired,
};

export default SalespersonsContent;
